import { Icons, Segmented, Switch, UI_SIZE_CHOICES, type UiSize } from '@workflow/ui'
import { SettingsRow } from '@/shell/Rows'
import { setAppearance, useAppearance, type Theme } from '@/features/appearance/appearance'

/**
 * Appearance — preferences of this browser, one sheet of bare rows.
 *
 * Text size is a set of steps, not a number field, and a step moves the whole
 * scale rather than one element — that is what keeps the icon pairing intact:
 * every step in the scale is a delta on this value, so an icon beside 13px
 * text is still the icon that was chosen to sit beside 13px text. A free-form
 * px input would let someone set UI text below its paired icon and break the
 * one rule the type system has.
 *
 * There is no reduced-motion switch. The OS already has one and the app
 * already obeys it, and a second control that can disagree with the first is
 * a bug with a settings row. The ambient light is ours, so its switch is too.
 */
export function AppearancePane() {
  const appearance = useAppearance()
  return (
    <div>
      <SettingsRow title="Theme" detail="System follows your operating system.">
        <Segmented<Theme>
          label="Theme"
          value={appearance.theme}
          onChange={(theme) => setAppearance({ theme })}
          options={[
            { value: 'system', label: 'System', icon: <Icons.themeSystem /> },
            { value: 'light', label: 'Light', icon: <Icons.themeLight /> },
            { value: 'dark', label: 'Dark', icon: <Icons.themeDark /> },
          ]}
        />
      </SettingsRow>
      <SettingsRow title="Text size" detail="Scales text, icons and controls together.">
        <Segmented<UiSize>
          label="Text size"
          value={appearance.uiSize}
          onChange={(uiSize) => setAppearance({ uiSize })}
          options={UI_SIZE_CHOICES.map((size) => ({ value: size, label: `${size}px` }))}
        />
      </SettingsRow>
      <SettingsRow title="Ambient light" detail="The drifting pools of light behind the page.">
        <Switch
          label="Ambient light"
          checked={appearance.ambient}
          onChange={(ambient) => setAppearance({ ambient })}
        />
      </SettingsRow>
      <p className="pt-2 text-caption text-muted-foreground">These preferences are saved in this browser only.</p>
    </div>
  )
}
